// src/pages/Settings.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import { useMsal, useAccount, useIsAuthenticated } from "@azure/msal-react";
import { connectGitHub, getGitHubStatus, disconnectGitHub } from "../utils/api";

export default function Settings() {
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isAuthenticated = useIsAuthenticated();
  const username =
    account?.username || account?.idTokenClaims?.emails?.[0] || "—";
  const name =
    account?.name ||
    account?.idTokenClaims?.name ||
    account?.idTokenClaims?.given_name ||
    "User";

  // GitHub integration state
  const [githubStatus, setGitHubStatus] = useState(null);
  const [githubLoading, setGitHubLoading] = useState(true);

  useEffect(() => {
    const loadGitHubStatus = async () => {
      try {
        const status = await getGitHubStatus();
        setGitHubStatus(status);
      } catch (e) {
        console.error("Failed to load GitHub status:", e);
        setGitHubStatus({ connected: false });
      } finally {
        setGitHubLoading(false);
      }
    };
    loadGitHubStatus();
  }, []);

  return (
    <Stack spacing={3}>
      {/* Page title */}
      <Box>
        <Typography variant="h4" fontWeight={700}>
          Settings
        </Typography>
        <Typography variant="paragraph" color="text.secondary">
          Manage your account preferences.
        </Typography>
      </Box>

      {/* Account */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Account
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>
              {name}
            </Typography>
            <Typography variant="paragraph" color="text.secondary">
              {username}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* GitHub Integration */}
      <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          GitHub Integration
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {githubLoading ? (
          <Typography>Loading...</Typography>
        ) : githubStatus?.connected ? (
          <Stack spacing={2}>
            <Typography>
              Connected as <strong>{githubStatus.github_login}</strong>
            </Typography>
            <Button 
              variant="outlined" 
              color="error" 
              onClick={async () => {
                try {
                  await disconnectGitHub();
                  setGitHubStatus({ connected: false });
                } catch (e) {
                  console.error("Failed to disconnect GitHub:", e);
                }
              }}
            >
              Disconnect GitHub
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            disabled={!isAuthenticated}
            onClick={async () => {
              if (!isAuthenticated) {
                alert("Please log in first before connecting GitHub.");
                return;
              }
              try {
                console.log("Connecting to GitHub...");
                const response = await connectGitHub();
                console.log("GitHub connect response:", response);
                const authorize_url = response?.authorize_url;
                if (authorize_url) {
                  console.log("Redirecting to GitHub:", authorize_url);
                  window.location.href = authorize_url;
                } else {
                  console.error("No authorize_url in response:", response);
                  alert("Failed to get GitHub authorization URL. Please check console for details.");
                }
              } catch (e) {
                console.error("Failed to connect GitHub:", e);
                console.error("Error details:", {
                  message: e?.message,
                  response: e?.response?.data,
                  status: e?.response?.status,
                });
                const errorMsg = e?.response?.data?.error || e?.message || "Unknown error";
                if (errorMsg === "REDIRECTING_FOR_AUTH") {
                  // MSAL is handling redirect, don't show error
                  console.log("MSAL redirecting for authentication...");
                  return;
                }
                alert(`Failed to connect GitHub: ${errorMsg}\n\nCheck browser console for more details.`);
              }
            }}
          >
            Connect GitHub
          </Button>
        )}
      </Paper>

    </Stack>
  );
}